import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { PortalHeader } from '../components/shell/PortalHeader';
import { PricingStatusBadge, PricingStatusNote } from '../components/loan/PricingStatusNote';
import { AnimatedCheck } from '../components/ui/AnimatedCheck';
import { Button } from '../components/ui/Button';
import { usePortal } from '../contexts/PortalContext';
import { useTokens } from '../hooks/useTokens';
import { loanOptions } from '../data/loanOptions';
import { formatCurrency } from '../utils/format';
const ease = [0.23, 1, 0.32, 1] as const;
const nextSteps = [{
  title: 'Upload a few documents',
  detail: 'We’ve tailored a short list to your application. About 10 minutes.',
  now: true
}, {
  title: '{{loanOfficerFirstName}} confirms your pricing',
  detail: 'Your rate isn’t locked yet. {{loanOfficerFirstName}} will review final pricing with you before locking.'
}, {
  title: 'Track progress here',
  detail: 'This same link becomes your loan dashboard. We’ll text you when something changes.'
}];
export function Complete() {
  const navigate = useNavigate();
  const {
    selectedOption,
    answers,
    submitted
  } = usePortal();
  const {
    fill
  } = useTokens();
  const option = loanOptions.find(o => o.id === selectedOption);
  if (!submitted || !option) return <Navigate to="/submit" replace data-id="element-762" data-mp="ok3twize9x0c" />;
  return <div className="min-h-screen w-full bg-canvas" data-id="element-763" data-mp="olwyztud0968">
      <PortalHeader maxWidth="max-w-3xl" data-id="element-764" data-mp="jtvpgfokswjw" />
      <main className="mx-auto max-w-[560px] px-5 pb-20 pt-12 md:pt-20" data-id="element-765" data-mp="js2lj85ckqfl">
        <AnimatedCheck size={64} data-id="element-766" data-mp="fr8tc2kvgpgw" />
        <motion.h1 initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease,
        delay: 0.25
      }} className="mt-8 text-[32px] font-semibold leading-tight tracking-[-0.03em] text-ink-900 md:text-[44px]" data-id="element-767" data-mp="fsa6qdpd1o1s">
          Application submitted.
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease,
        delay: 0.3
      }} className="mt-3 text-[17px] leading-relaxed text-ink-500" data-id="element-768" data-mp="vb3f21jkhd4">
          Nice work, {answers.firstName}. Your loan team has everything they need to get started.
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease,
        delay: 0.35
      }} className="mt-8 rounded-2xl bg-white p-5 ring-1 ring-ink-200" data-id="element-769" data-mp="3edocw4iqdido">
          <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-770" data-mp="9y6ofnj3d9i4">
            <p className="font-medium text-ink-900" data-id="element-771" data-mp="evotv3xwh6ld">
              {option.name} · {option.rate.toFixed(3)}% · {formatCurrency(option.monthly)}/mo
            </p>
            <PricingStatusBadge data-id="element-772" data-mp="56opf50mpizs" />
          </div>
          <div className="mt-2" data-id="element-773" data-mp="55dm16b3id97">
            <PricingStatusNote data-id="element-774" data-mp="54kdbzsktdof" />
          </div>
        </motion.div>

        <motion.section initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.3,
        delay: 0.4
      }} className="mt-12" aria-labelledby="next-title" data-id="element-775" data-mp="suy9o2i71yot">
          <h2 id="next-title" className="text-sm font-medium text-ink-900" data-id="element-776" data-mp="2icv0zq4nuesg">
            What happens next
          </h2>
          <ol className="relative mt-5 space-y-7 border-l border-ink-200 pl-7" data-id="element-777" data-mp="2dds977doy52f">
            {nextSteps.map((s, i) => <motion.li key={s.title} initial={{
            opacity: 0,
            x: -6
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.25,
            ease,
            delay: 0.45 + i * 0.06
          }} className="relative" data-id="element-778" data-mp="2s2oh94fqzi31" data-mpi={i}>
                <span className={`absolute -left-[33px] top-1 h-3 w-3 rounded-full ring-4 ring-canvas ${s.now ? 'bg-pulse-600' : 'bg-ink-300'}`} aria-hidden data-id="element-779" data-mp="1zfam9bvm0wlk" />
                <p className={`font-medium ${s.now ? 'text-ink-900' : 'text-ink-700'}`} data-id="element-780" data-mp="29dht31w6py9u">{fill(s.title)}</p>
                <p className="mt-1 text-sm leading-relaxed text-ink-500" data-id="element-781" data-mp="24dlwvotqbb4u">{fill(s.detail)}</p>
              </motion.li>)}
          </ol>
        </motion.section>

        <Button size="lg" className="mt-12 w-full sm:w-auto" onClick={() => navigate('/documents')} trailingIcon={<ArrowRightIcon className="h-5 w-5" data-id="element-783" data-mp="1pc6nln14j7a8" />} data-id="element-782" data-mp="3ft0u8tpuo50h">
          Start uploading documents
        </Button>
      </main>
    </div>;
}